import React, {useState} from 'react';
import {classes, style} from './doppe-layout-root.st.css';
import {useDoppeViewerLangDictionary} from '../../../doppe-viewer-lang-dictionary/doppe-viewer-lang-dictionary';
import {DoppePageFooterBanner} from '../../../doppe-page-footer-banner/doppe-page-footer-banner';
import {FloatingWindowOpener, InnerWindowAnimator} from '@wix/devzai-utils-react';
import {
    DoppeViewerDefaultNotificationContent
} from '../../../doppe-viewer-default-notification-content/doppe-viewer-default-notification-content';
import {
    useDoppeViewerAppUiServiceComponentRef
} from '../../../../client/doppe-viewer-app-client-services/doppe-viewer-app-ui-service';
import {
    DoppePagePrivacyAndTermsView
} from '../../../doppe-page-privacy-and-terms-view/doppe-page-privacy-and-terms-view';
import {useBymoPageContextProps} from '../../../../doppe-sdk';
import {
    DoppePageCookieBannerOpener
} from '../../../doppe-page-cookie-consent/doppe-page-cookie-banner-opener/doppe-page-cookie-banner-opener';
import {BymoPageViewerMode, useBymoPageViewerContext} from "../../../bymo-page-viewer-context/bymo-page-viewer-context";

export const DoppeLayoutRoot = React.memo(function DoppeLayoutDefaultProfileView(props: DoppeLayoutRoot.Props) {
    const {
        className,
        children,

        hideFooterBanner = false,
        fullScreen = false,
        ...htmlAttributes
    } = props;
    
    const viewerLang = useDoppeViewerLangDictionary();
    const bymoPageContextProps = useBymoPageContextProps();
    const pageViewerContext = useBymoPageViewerContext();

    const defaultNotificationOpenerRef = useDoppeViewerAppUiServiceComponentRef('defaultNotificationOpener');
    const [_defaultNotificationIsShown, setDefaultNotificationIsShown] = useState(false);

    const cookieBannerEnabled = (bymoPageContextProps.userCookieBannerSettings?.cookieBannerEnabled)
        && (pageViewerContext.mode === BymoPageViewerMode.Live);
    const userPrivacyPolicyUrl = (bymoPageContextProps.userPrivacyPolicyUrl ?? '').trim();
    const userTermsOfServiceUrl = (bymoPageContextProps.userTermsOfServiceUrl ?? '').trim();
    const hasPrivacyAndTerms = (userPrivacyPolicyUrl || userTermsOfServiceUrl);

    return (
        <div
            {...htmlAttributes}
            className={style(classes.root, {
                hideFooterBanner: hideFooterBanner,
                hideCookieBanner: !cookieBannerEnabled,
                hidePrivacyAndTerms: !hasPrivacyAndTerms,
                fullScreen: fullScreen,
            }, className)}
        >
            <FloatingWindowOpener
                ref={defaultNotificationOpenerRef}
                onOpen={() => {
                    setDefaultNotificationIsShown(true);
                }}
                onClose={() => {
                    setDefaultNotificationIsShown(false);
                }}
            >
                {(renderProps) => {

                    return (
                        <InnerWindowAnimator
                            windowClosingControllerRef={renderProps.windowClosingControllerRef}
                            openTransition={{
                                animation: [
                                    {transform: 'translate(0, -100%)'},
                                    {transform: 'translate(0, 0)'},
                                ],
                                durationMilliseconds: 200,
                            }}
                            closeTransition={{
                                animation: [
                                    {transform: 'translate(0, 0)'},
                                    {transform: 'translate(0, -100%)'},
                                ],
                                durationMilliseconds: 200
                            }}
                        >
                            <div
                                className={style(classes.defaultNotificationContainer, {})}
                            >
                                <DoppeViewerDefaultNotificationContent
                                    className={style(classes.defaultNotification, classes.floatingViewShadow)}
                                    {...renderProps.contextProps}
                                />
                            </div>
                        </InnerWindowAnimator>
                    )
                }}
            </FloatingWindowOpener>
            <div
                className={style(classes.main)}
            >
                <div
                    className={style(classes.mainContent, {})}
                >
                    <div className={style(classes.backgroundWrapper, {})}>
                        {children}
                    </div>
                </div>
                {
                    hasPrivacyAndTerms ?
                        <div
                            className={style(classes.privacyAndTermsContainer, {})}
                        >
                            <DoppePagePrivacyAndTermsView
                                className={style(classes.privacyAndTerms, {})}
                            />
                        </div>
                        :
                        null
                }
                {
                    cookieBannerEnabled ?
                        <DoppePageCookieBannerOpener
                            className={style(classes.cookieBanner, {})}
                        /> : null
                }
                {
                    !hideFooterBanner ?
                    <DoppePageFooterBanner
                        className={style(classes.contentFooter, {})}
                        bannerMessage={viewerLang.claim}
                        targetUrl={`https://www.hopp.co`}
                    />
                    :
                    null
                }
            </div>
        </div>
    )
});

export namespace DoppeLayoutRoot {

    export interface Props extends React.HTMLAttributes<any> {
        hideFooterBanner?: boolean;
        fullScreen?: boolean;
    }
}